import { useDebounce } from "@react-hook/debounce";
import { createContext, ReactNode, useState } from "react";

import { useRouter } from "next/router";
import queryString from "query-string";
import { useFilters } from "src/functions/useFilters";
import useGetDataSearchInstructors from "src/functions/useGetDataSearchInstructors";
import useGetDataSearchContent from "src/functions/useGetDataSearchContent";

export const SearchContext = createContext<{
  query: string;
  setQuery: (str: string) => void;
  instructors;
  lectures;
}>({
  query: "",
  setQuery: () => {},
  instructors: null,
  lectures: null,
});

type Props = {
  children: ReactNode;
};

export function SearchContextProvider({ children }: Props) {
  const { push, replace, pathname, query } = useRouter();

  const combinedQuery =
    typeof location !== "undefined"
      ? queryString.parse(location.search)
      : query;

  const urlSearchQuery: string =
    typeof combinedQuery.q === "string" ? combinedQuery.q : "";

  const [searchQuery, setSearchQuery] = useState(urlSearchQuery);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useDebounce(
    urlSearchQuery,
    400
  );

  const filtersState = useFilters();

  const lectures = useGetDataSearchContent(
    filtersState.value,
    debouncedSearchQuery,
    !debouncedSearchQuery
  );

  const instructors = useGetDataSearchInstructors(
    debouncedSearchQuery,
    !debouncedSearchQuery
  );

  const sharedProps = {
    query: searchQuery,
    setQuery: (newQuery: string) => {
      setSearchQuery(newQuery);
      if (pathname === "/search") {
        replace("/search?q=" + newQuery);
      } else {
        if (newQuery) {
          push("/search?q=" + newQuery).then(() => {
            const el = document.getElementById("global-search-input");
            el.focus();
          });
        }
      }

      setDebouncedSearchQuery(newQuery);
    },
    lectures,
    instructors,
  };

  return (
    <SearchContext.Provider value={sharedProps}>
      {children}
    </SearchContext.Provider>
  );
}
